import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, X } from "lucide-react";
import { MoreVert } from "@mui/icons-material";
import {
  getRadiologyApi,
  updateRadiologyApi,
  deleteRadiologyByIdApi,
} from "../services/radiology.service";
import { hasPermission } from "../utils/permissionHelper";
import { useGetApi } from "../hooks/useGetApi";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import dayjs from "dayjs";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
const buttonStyle = {};

export default function Radiology() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);
  const [dateFilter, setDateFilter] = useState("all");
  const [activeButton, setActiveButton] = useState("All Reports");
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const user = useSelector((state) => state.user);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter popover options based on permissions
  const getFilteredPopoverOptions = () => {
    let options = ["Cancel Report"];
    if (hasPermission(user, "Radiology.Delete")) {
      options.push("Delete");
    }
    return options;
  };

  // Fetch all radiology requests for client-side sorting and pagination (like Consultation page)
  const {
    dataList: radiologyList,
    dataCount: radiologyCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getRadiologyApi,
    params: {
      limit: 10000, // Fetch large number for client-side pagination/sorting
      page: 1,
      search,
      dateFilter,
      status: activeButton === "All Reports" ? "" : activeButton,
      // Remove sort/order - will be handled client-side
    },
    dependencies: [search, activeButton, dateFilter],
    // Remove paginationModel and sortModel from dependencies - handled client-side
    debounceDelay: 500,
    dataKey: "radiologyRequests",
    countKey: "total",
  });

  const button = [
    { label: "All Reports", width: "140px" },
    { label: "Pending", width: "130px" },
    { label: "In Progress", width: "140px" },
    { label: "Dispatched", width: "140px" },
    { label: "Cancelled", width: "140px" },
  ];

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };
  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const cancelReportHandler = async () => {
    if (
      selectedRow?.status === "Dispatched" ||
      selectedRow?.status === "Cancelled"
    ) {
      toast.info("This report is already dispatched or cancelled");
      setSelectedRow(null);
      return;
    }
    let response;
    setLoading(true);
    response = await updateRadiologyApi(selectedRow?._id, {
      status: "Cancelled",
    });
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success("Report cancelled successfully");
      setSelectedRow(null);
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
      setSelectedRow(null);
    }
  };

  const deleteReportHandler = async () => {
    if (!selectedRow?._id) {
      toast.error("No report selected");
      setSelectedRow(null);
      return;
    }

    let response;
    setLoading(true);
    response = await deleteRadiologyByIdApi(selectedRow?._id);
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success("Radiology request deleted successfully");
      setSelectedRow(null);
      refetch();
    } else {
      toast.error(response?.message || "Failed to delete radiology request");
      setSelectedRow(null);
    }
  };

  const handleMenuClick = (option) => {
    // Close popover immediately but preserve selectedRow for the operation
    setAnchorEl(null);
    
    if (option === "Cancel Report") {
      cancelReportHandler();
    } else if (option === "Delete") {
      deleteReportHandler();
    }
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row);
  };

  // Prepare rows for DataGrid
  const rows = radiologyList?.map((row) => {
    return {
      id: row._id,
      _id: row._id,
      createdDate: row.createdAt || row.createdDate,
      testName: row?.service?.itemName || "-",
      UHID: row?.UHID || "-",
      patientName: `${row?.patient?.firstName || ""} ${row?.patient?.lastName || ""}`.trim(),
      wardName: row?.ward?.wardName || "-",
      bedNumber: row?.bedNumber?.bedNumber || "-",
      doctorName: `${row?.doctor?.firstName || ""} ${row?.doctor?.lastName || ""}`.trim(),
      status: row?.status || "-",
      patientId: row?.patient?._id,
      service: row?.service,
      patient: row?.patient,
      ward: row?.ward,
      doctor: row?.doctor,
    };
  }) || [];

  // Compute status counts when viewing all reports
  const statusCountsRef = useRef({});
  useMemo(() => {
    if (activeButton === "All Reports" && radiologyList?.length > 0) {
      const counts = { "All Reports": radiologyList.length };
      radiologyList.forEach((row) => {
        const s = row?.status;
        if (s) counts[s] = (counts[s] || 0) + 1;
      });
      statusCountsRef.current = counts;
    }
  }, [activeButton, radiologyList]);

  // Responsive columns configuration
  const getColumns = useCallback(() => {
    const baseColumns = [
      {
        field: 'createdDate',
        headerName: 'Date',
        flex: isMobile ? 0 : 1,
        width: isMobile ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        valueFormatter: (params) => {
          return params.value ? dayjs(params.value).format("DD/MM/YYYY, hh:mm A") : "-";
        },
      },
      {
        field: 'testName',
        headerName: 'Test Name',
        flex: isMobile ? 0 : 1.2,
        width: isMobile ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => (
          <Box
            component={params.row.status !== "Cancelled" ? Link : "div"}
            to={params.row.status !== "Cancelled" ? `/radiology/patient/${params.row._id}` : undefined}
            sx={{
              color: params.row.status !== "Cancelled" ? "primary.main" : "text.disabled",
              cursor: params.row.status !== "Cancelled" ? "pointer" : "default",
              textDecoration: params.value ? "underline" : "none",
              pointerEvents: params.row.status === "Cancelled" ? "none" : "auto",
              fontSize: isMobile ? '12px' : '14px',
            }}
          >
            {params.value || "-"}
          </Box>
        ),
      },
      {
        field: 'UHID',
        headerName: 'UHID',
        flex: isMobile ? 0 : 0.8,
        width: isMobile ? 100 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'patientName',
        headerName: 'Name',
        flex: isMobile ? 0 : 1.2,
        width: isMobile ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'wardName',
        headerName: 'Ward Name',
        flex: isTablet ? 0 : 0.8,
        width: isTablet ? 100 : undefined,
        minWidth: 100,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'bedNumber',
        headerName: 'Bed Number',
        flex: isTablet ? 0 : 0.7,
        width: isTablet ? 90 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'doctorName',
        headerName: 'Doctor Name',
        flex: isTablet ? 0 : 1,
        width: isTablet ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: isTablet ? 0 : 0.8,
        width: isTablet ? 100 : undefined,
        minWidth: 100,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => (
          <Typography
            sx={{
              color:
                params.value === "In Progress"
                  ? "#2D23BA"
                  : params.value === "Accepted"
                  ? "#13A2C2"
                  : params.value === "Cancelled"
                  ? "#E00700"
                  : params.value === "Pending"
                  ? "#DA9304"
                  : params.value === "Dispatched"
                  ? "#61AD1A"
                  : "inherit",
            }}
          >
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0,
        width: 70,
        minWidth: 60,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="actions"
            size={isMobile ? "small" : "medium"}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e, params.row);
            }}
          >
            <MoreVert fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        ),
      },
    ];

    // Responsive column hiding
    if (isMobile) {
      return baseColumns.filter(col => 
        ['createdDate', 'testName', 'patientName', 'actions'].includes(col.field)
      );
    }
    if (isTablet) {
      return baseColumns.filter(col => 
        ['createdDate', 'testName', 'UHID', 'patientName', 'status', 'actions'].includes(col.field)
      );
    }
    return baseColumns;
  }, [isMobile, isTablet]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              marginBottom: "40px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search by UHID"
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Filter: Date dropdown on left, status buttons on right */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "40px",
            }}
          >
            <FormControl size="small" sx={{ minWidth: "100px" }}>
              <InputLabel id="date-select-label">Date</InputLabel>
              <Select
                labelId="date-select-label"
                label="Date"
                value={dateFilter || ""}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {button.map((btn, i) => (
                  <Button
                    key={i}
                    variant="outlined"
                    sx={{
                      border: `2px solid ${
                        activeButton === btn.label
                          ? theme.palette.primary.dark
                          : "#c0c0c0"
                      }`,
                      color:
                        activeButton === btn.label
                          ? theme.palette.primary.dark
                          : "text.secondary",
                      bgcolor:
                        activeButton === btn.label ? "#AED5F3" : "#F0F7FC",
                      fontWeight: activeButton === btn.label ? 600 : 400,
                      width: btn.width || "130px",
                      whiteSpace: "nowrap",
                      textTransform: "none",
                    }}
                    onClick={() => setActiveButton(btn.label)}
                  >
                    {btn.label}{statusCountsRef.current[btn.label] !== undefined ? ` (${statusCountsRef.current[btn.label]})` : ""}
                  </Button>
              ))}
            </Box>
          </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                marginRight: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
                height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" },
                width: '100%',
                minHeight: '300px',
                maxWidth: '100%',
                overflow: 'hidden'
              }}
            >
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="client"
                sortingMode="client"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                loading={isLoading}
                onCellClick={(params, event) => {
                  if (params.field !== 'actions') {
                    handleClick(event, params.row);
                  }
                }}
                onRowContextMenu={(params, event) => {
                  event.preventDefault();
                  handleRightClick(event, params.row);
                }}
                componentsProps={{
                  row: {
                    onContextMenu: (event) => {
                      event.preventDefault();
                    },
                  },
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    width: '100%',
                  },
                  '& .MuiDataGrid-main': {
                    width: '100%',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                    padding: isMobile ? '4px' : '8px',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      cursor: 'pointer',
                    },
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#fafafa',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'white',
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '200px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    '& .MuiDataGrid-columnHeader': {
                      fontSize: '11px',
                      padding: '4px',
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: '11px',
                      padding: '2px 4px',
                    },
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        No radiology reports found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Menu
        id={Boolean(anchorEl) ? "row-popover" : undefined}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={
          anchorPosition
            ? { top: anchorPosition.top, left: anchorPosition.left }
            : undefined
        }
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
              maxHeight: "300px",
            },
          },
        }}
      >
        {getFilteredPopoverOptions()?.map((option, index) => (
          <MenuItem key={index} onClick={() => handleMenuClick(option)}>
            {loading ? <CircularProgress size={20} /> : option || ""}
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
}
